/**
 * @file
 * The shared toast stack (berTags.toast): any component pushes a short
 * message, optionally with action buttons and a countdown, and they stack
 * bottom-center instead of overlapping.
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { el, button, textSpan, onClick } = berTags.dom;

  const toaster = (() => {
    let stack = null;
    // Open toasts, plus the keyed ones so a repeat push refills the toast
    // already on screen (a burst of Copied! toasts stays one toast).
    const closers = new Set();
    const keyed = new Map();

    const container = () => {
      if (!stack) {
        stack = el('div', 'ber-toast-stack');
        document.body.appendChild(stack);
      }
      return stack;
    };

    // opts: {message, seconds, countdown, actions:[{label,onClick}],
    // onExpire, role, key}. Returns {close}.
    const push = (opts) => {
      const live = opts.key ? keyed.get(opts.key) : null;
      if (live) {
        live.apply(opts, true);
        return live.handle;
      }

      const { key } = opts;
      const toast = el('div', 'ber-toast');
      const msg = textSpan('ber-toast__msg', '');
      toast.appendChild(msg);
      // The canceller rather than the handle, so a countdown interval and
      // a plain timeout are both stopped by the right function.
      let cancelTimer = null;
      let closed = false;
      let entry = null;

      const stopTimer = () => {
        if (cancelTimer) {
          cancelTimer();
          cancelTimer = null;
        }
      };

      const close = () => {
        if (closed) {
          return;
        }
        closed = true;
        stopTimer();
        closers.delete(close);
        if (key && keyed.get(key) === entry) {
          keyed.delete(key);
        }
        toast.classList.remove('is-open');
        // Off the DOM once the fade-out is over.
        setTimeout(() => toast.remove(), 300);
      };

      // Fills the toast, and refills it on a keyed repeat: the element
      // stays mounted and in place, so a burst never leaves the outgoing
      // toast fading above the incoming one.
      const apply = (next, refill) => {
        stopTimer();
        toast.setAttribute('role', next.role || 'status');
        msg.textContent = next.message || '';
        if (refill) {
          // Re-adding the class alone is not a new animation to the
          // engine, the reflow between the two is what restarts it.
          toast.classList.remove('is-pulsing');
          toast.getBoundingClientRect();
          toast.classList.add('is-pulsing');
        }
        while (toast.lastChild !== msg) {
          toast.removeChild(toast.lastChild);
        }

        (next.actions || []).forEach((action) => {
          const btn = button('ber-toast__btn');
          btn.textContent = action.label;
          onClick(btn, () => {
            close();
            if (action.onClick) {
              action.onClick();
            }
          });
          toast.appendChild(btn);
        });

        let left = parseInt(next.seconds, 10) || 0;
        if (left <= 0) {
          return;
        }

        const expire = () => {
          close();
          if (next.onExpire) {
            next.onExpire();
          }
        };

        if (next.countdown) {
          const count = textSpan('ber-toast__count', `${left}s`);
          count.setAttribute('aria-hidden', 'true');
          toast.appendChild(count);
          const id = setInterval(() => {
            left -= 1;
            if (left <= 0) {
              expire();
              return;
            }
            count.textContent = `${left}s`;
          }, 1000);
          cancelTimer = () => clearInterval(id);
        } else {
          const id = setTimeout(expire, left * 1000);
          cancelTimer = () => clearTimeout(id);
        }
      };

      const handle = { close };
      entry = { apply, close, handle };
      closers.add(close);
      if (key) {
        keyed.set(key, entry);
      }

      apply(opts);
      container().appendChild(toast);
      // Next frame, so the slide-in transition plays.
      requestAnimationFrame(() => toast.classList.add('is-open'));

      return handle;
    };

    // Drops every open toast at once.
    const hide = () => {
      Array.from(closers).forEach((close) => close());
    };

    return { push, hide };
  })();

  berTags.toast = toaster;
})(Drupal);
